import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { BLOG_DOC_NAME, db } from '../../../../app/firebase/firebaseConfig';
import BlogForm from './BlogForm';

interface BlogFormData extends FormData {
  get(name: string): string | File | null;
}

const EditBlog: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Get the blog document
        const blogDoc = await getDoc(doc(db, BLOG_DOC_NAME, id));
        
        if (!blogDoc.exists()) {
          setError('Blog not found');
          return;
        }
        
        // Set the blog data
        setBlog({
          id: blogDoc.id,
          ...blogDoc.data()
        });
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBlog();
  }, [id]);

  const handleSubmit = async (formData: BlogFormData) => {
    if (!id) return;
    
    try {
      setUpdating(true);
      
      // Create an object with the updated blog data
      const updatedBlog: {
        title: string;
        subtitle: string;
        description: string;
        categories: any;
        hashtags: any;
        updatedAt: Date;
        imageUrl?: string;
      } = {
        title: formData.get('title') as string,
        subtitle: formData.get('subtitle') as string,
        description: formData.get('description') as string,
        categories: JSON.parse(formData.get('categories') as string),
        hashtags: JSON.parse(formData.get('hashtags') as string),
        updatedAt: new Date(),
      };
      
      // If there's a new image URL, add it to the update
      const imageUrl = formData.get('imageUrl');
      if (imageUrl && typeof imageUrl === 'string') {
        updatedBlog.imageUrl = imageUrl;
      }
      
      // Update the document in Firestore
      await updateDoc(doc(db, BLOG_DOC_NAME, id), updatedBlog);
      
      // Navigate back to the blog list or the blog detail page
      navigate('/dashboard');
    } catch (err) {
      console.error('Error updating blog:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while updating the blog');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
        <button 
          className="mt-3 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!blog) {
    return <div className="text-white">Blog not found</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-darkGreyPrimary h-screen overflow-y-auto">
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate('/dashboard')} 
          className="mr-4 text-indigo-400 hover:text-indigo-300"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-white">Edit Blog Post</h1>
      </div>
      
      <BlogForm 
        initialData={blog} 
        onSubmit={handleSubmit} 
        isUpdating={updating}
      />
    </div>
  );
};

export default EditBlog; 